import React from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { EnterRouteFormProps, WaypointPredicate } from './types';
import { ArcButton, ArcIconButton, ArcMenuItem, ArcOutlinedInput, ArcOutlinedSelect, ArcStack, ArcText } from '@src/core/ui';
import { Controller, useFieldArray } from 'react-hook-form';
import { REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { SettingUnitType, useGetSettingByUnitType } from '@src/core/features';
import { useDispatch, useSelector } from 'react-redux';
import { selectEditableRoute, selectTerrainMode, selectWaypoints, setEditableRoute, setTerrainMode, setWaypoints, useGetTerrainAwareMutation } from '@src/features/order/pages/upsert-mission-page/redux';
import { TerrainMode } from '@src/features/order/pages/upsert-mission-page/redux/extra-store/types';
import { RouteWaypointEntity, RouteWaypointType } from '@src/features/order/pages/upsert-mission-page/entities';
import { setMapCenter, useConfirm } from '@src/core/controllers';
import { arcSpatiumUnit, arcVelocitasUnit } from '@src/core/extensions';
import { InputAdornment } from '@mui/material';
import TerrainAlert from '@src/features/order/pages/upsert-mission-page/components/terrain-alert';
import { terrainCacheKey } from '@src/features/order/pages/upsert-mission-page/config/cache-keys';
import { routesConfig } from '@src/core/routes';
import { useNavigate } from 'react-router-dom';
import { useNumericRanges } from '@src/core/features/settings';

const EnterRouteForm: React.FC<EnterRouteFormProps> = (props) => {
  const { form, onSubmit, isSubmitting, isCanceling, goBack } = props;
  
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const wps = useSelector(selectWaypoints);
  const editableRoute = useSelector(selectEditableRoute);
  const terrainMode = useSelector(selectTerrainMode);
  const isAdvanced = terrainMode === TerrainMode.ADVANCED;
  const isAboveTerrain = terrainMode === TerrainMode.ABOVE_TERRAIN;
  const deliveyMode = useGetSettingByUnitType(SettingUnitType.DELIVERY_MODE);
  const numericRanges = useNumericRanges();

  const [, { data: terrainData = [] }] = useGetTerrainAwareMutation({ fixedCacheKey: terrainCacheKey });

  const getAmslApprox = React.useCallback((aglValue: string | number, terrainAlt: number) => {
    const agl = Number(aglValue);
    if (isNaN(agl) || isNaN(terrainAlt)) return null;
    return Math.round(terrainAlt + agl);
  }, []);

  const takeoffTerrainAlt = terrainData[0] ? Number(terrainData[0].altitude) : null;
  const destinationTerrainAlt = terrainData.length > 0 ? Number(terrainData[terrainData.length - 1].altitude) : null;

  const { fields, replace } = useFieldArray({ control: form.control, name: 'waypoints' });

  const pureWps = React.useMemo(() => {
    return wps.filter((w) => w.type === RouteWaypointType.WAYPOINT);
  }, [wps]);


  const onChangeAltitde = (value: string, predicate: WaypointPredicate) => {
    dispatch(setWaypoints((wps.map((w: RouteWaypointEntity) => {
      if (predicate(w)) {
        return {
          ...w,
          altitude: value,
        };
      }
      return w;
    }))));
  };

  const onDeleteWp = async (uuid: string) => {
    if (await confirm('Are you sure you want to delete this waypoint?', 'Delete Waypoint'))
      dispatch(setWaypoints(wps.filter((wp) => wp.uuid !== uuid)));
  };

  const toggleEditableRoute = () => {
    dispatch(setEditableRoute(!editableRoute));
  };

  React.useEffect(() => {
    if (pureWps.length === fields.length) return;

    replace(wps.filter((w) => w.type === RouteWaypointType.WAYPOINT));
  }, [pureWps]);

  return (
    <ArcStack
      spacing={2}
      height='100%' 
      component='form' 
      justifyContent='space-between'
      onSubmit={form.handleSubmit(onSubmit)}>
      <ArcStack spacing={2}>
        <ArcOutlinedSelect
          label='Altitude Mode'
          value={terrainMode}
          onChange={(e) => dispatch(setTerrainMode(e.target.value as TerrainMode))}>
          <ArcMenuItem value={TerrainMode.BASIC}>Relative to Launch</ArcMenuItem>
          <ArcMenuItem value={TerrainMode.ADVANCED}>AMSL</ArcMenuItem>
          <ArcMenuItem value={TerrainMode.ABOVE_TERRAIN}>Above Terrain</ArcMenuItem>
        </ArcOutlinedSelect>

        <Controller 
          control={form.control} 
          name='takeOffAltitude' 
          rules={{ required: REQUIRED_VALIDATION_TEXT }}
          render={({ field, formState: { errors } }) => (
            <ArcOutlinedInput 
              {...field}
              type='number'
              label='Takeoff Altitude'
              error={Boolean(errors.takeOffAltitude)}
              helperText={errors.takeOffAltitude?.message} 
              minNumber={numericRanges.flightAltitude?.min}
              maxNumber={numericRanges.flightAltitude?.max}
              onChange={(e) => {
                field.onChange(e);
                onChangeAltitde(e.target.value, (w) => w.type === RouteWaypointType.TAKE_OFF);
              }}
              slotProps={{ input: { 
                endAdornment: (
                  <InputAdornment position='end'>
                    {isAboveTerrain && takeoffTerrainAlt != null && (
                      <ArcText.h5 sx={{ whiteSpace: 'nowrap', mr: 0.5 }} color='text.secondary'>
                        ≈ {getAmslApprox(field.value, takeoffTerrainAlt)} {arcSpatiumUnit()} (AMSL)
                      </ArcText.h5>
                    )}
                    <ArcText.h5>{arcSpatiumUnit()}</ArcText.h5> 
                  </InputAdornment> 
                )
              }}}
            />
          )} />


      
        <Controller 
          control={form.control} 
          name='destinationAltitude'
          rules={{ required: REQUIRED_VALIDATION_TEXT }}
          render={({ field, formState: { errors } }) => (
            <ArcOutlinedInput 
              {...field}
              type='number'
              label='Destination Altitude'
              error={Boolean(errors.destinationAltitude)}
              helperText={errors?.destinationAltitude?.message}
              minNumber={numericRanges.flightAltitude?.min}
              maxNumber={numericRanges.flightAltitude?.max}
              onChange={(e) => {
                field.onChange(e);
                onChangeAltitde(e.target.value, (w) => w.type === RouteWaypointType.DESTINATION);
              }}
              slotProps={{ input: { 
                endAdornment: (
                  <InputAdornment position='end'>
                    {isAboveTerrain && destinationTerrainAlt != null && (
                      <ArcText.h5 sx={{ whiteSpace: 'nowrap', mr: 0.5 }} color='text.secondary'>
                        ≈ {getAmslApprox(field.value, destinationTerrainAlt)} {arcSpatiumUnit()} (AMSL)
                      </ArcText.h5>
                    )}
                    <ArcText.h5>{arcSpatiumUnit()}</ArcText.h5> 
                  </InputAdornment> 
                )
              }}}
            />
          )} />

        <ArcStack>
          {fields.map((wp, index) => {
            const name = `waypoints.${index}.altitude` as const;
            return (
              <ArcStack key={wp.id} direction='row' spacing={1} mb={2}>
                <Controller 
                  name={name} 
                  key={wp.id} 
                  control={form.control} 
                  rules={{ required: REQUIRED_VALIDATION_TEXT }}
                  render={({ field, formState: { errors } }) => (
                    <ArcOutlinedInput 
                      {...field}
                      fullWidth
                      type='number'
                      label={`Waypoint Altitude ${index + 1}`}
                      error={Boolean(errors.waypoints?.[index]?.altitude)}
                      helperText={errors.waypoints?.[index]?.altitude?.message}
                      minNumber={numericRanges.flightAltitude?.min}
                      maxNumber={numericRanges.flightAltitude?.max}
                      onChange={(e) => {
                        field.onChange(e);
                        onChangeAltitde(e.target.value, (w) => w.uuid === wp.uuid);
                      }}
                      onFocus={() => {
                        const lat = Number(wp.latitude);
                        const lng = Number(wp.longitude);

                        if (isNaN(lat) || isNaN(lng)) return;

                        dispatch(setMapCenter({ lat, lng }));
                      }}
                      slotProps={{ input: { 
                        endAdornment: (
                          <InputAdornment position='end'> 
                            <ArcText.h5>{arcSpatiumUnit()}</ArcText.h5> 
                          </InputAdornment> 
                        )
                      }}}
                    />
                  )}/>
                {editableRoute &&
                <ArcIconButton sx={{ width: 40, height: 40 }} onClick={() => onDeleteWp(wp.uuid)}>
                  <DeleteOutlineIcon color='error' fontSize='small' />
                </ArcIconButton>
                }
              </ArcStack>
            );
          })}

          <ArcStack sx={{ pb: '16px' }}>
            <TerrainAlert />
          </ArcStack>
        </ArcStack>
        

        <Controller 
          control={form.control} 
          name='deliveryAltitude' 
          rules={{ required: REQUIRED_VALIDATION_TEXT }}
          render={({ field, formState: { errors } }) => (
            <ArcOutlinedInput 
              {...field}
              type='number'
              label='Delivery Altitude'
              error={Boolean(errors.deliveryAltitude)}
              helperText={errors?.deliveryAltitude?.message}
              minNumber={numericRanges.deliveryAltitude?.min}
              maxNumber={numericRanges.deliveryAltitude?.max}
              slotProps={{ input: { 
                endAdornment: (
                  <InputAdornment position='end'>
                    {isAboveTerrain && destinationTerrainAlt != null && (
                      <ArcText.h5 sx={{ whiteSpace: 'nowrap', mr: 0.5 }} color='text.secondary'>
                        ≈ {getAmslApprox(field.value, destinationTerrainAlt)} {arcSpatiumUnit()} (AMSL)
                      </ArcText.h5>
                    )}
                    <ArcText.h5>{arcSpatiumUnit()}</ArcText.h5> 
                  </InputAdornment> 
                )
              }}} />
          )} />

        {isAdvanced && (
          <Controller 
            control={form.control} 
            name='winchAltitude' 
            rules={{ required: REQUIRED_VALIDATION_TEXT }}
            render={({ field, formState: { errors } }) => (
              <ArcOutlinedInput 
                {...field}
                type='number'
                label='Winch Altitude'
                error={Boolean(errors.winchAltitude)}
                helperText={errors?.winchAltitude?.message}
                minNumber={numericRanges.deliveryAltitude?.min}
                maxNumber={numericRanges.deliveryAltitude?.max}
                slotProps={{ input: { 
                  endAdornment: (
                    <InputAdornment position='end'> 
                      <ArcText.h5>{arcSpatiumUnit()}</ArcText.h5> 
                    </InputAdornment> 
                  )
                }}} />
            )} />
        )}

        <Controller 
          name='speed' 
          control={form.control} 
          rules={{ required: REQUIRED_VALIDATION_TEXT }}
          render={({ field, formState: { errors } }) => (
            <ArcOutlinedInput 
              {...field}
              type='number'
              label='Cruise Speed'
              error={Boolean(errors.speed)}
              helperText={errors?.speed?.message}
              minNumber={numericRanges.deliverySpeed?.min}
              maxNumber={numericRanges.deliverySpeed?.max}
              slotProps={{ input: { 
                endAdornment: (
                  <InputAdornment position='end'>
                    <ArcText.h5>{arcVelocitasUnit()}</ArcText.h5>
                  </InputAdornment> 
                )
              }}} />
          )} />


        <ArcStack spacing={2}>
          <ArcText.h4> MODE </ArcText.h4>
          <Controller 
            control={form.control} 
            name='deliveryMode'
            rules={{ required: REQUIRED_VALIDATION_TEXT }}
            render={({ field, formState: { errors } }) => (
              <ArcOutlinedSelect
                {...field}
                label='Delivery Mode'
                error={Boolean(errors.deliveryMode)}
                helperText={errors?.deliveryMode?.message}>
                {deliveyMode.values?.map((v) => (
                  <ArcMenuItem 
                    key={v.value} 
                    value={v.value}> 
                    {v.name} 
                  </ArcMenuItem>
                ))}
              </ArcOutlinedSelect>
            )} />
        </ArcStack>
      </ArcStack>
      <ArcStack spacing={1}>
        <ArcStack direction='row' spacing={1}>
          <ArcButton.ghost 
            fullWidth 
            isLoading={isCanceling} 
            disabled={editableRoute} 
            onClick={goBack}> 
            Cancel 
          </ArcButton.ghost>
          {editableRoute 
            ? <ArcButton.brand 
              fullWidth  
              onClick={toggleEditableRoute}> 
              Apply Route 
            </ArcButton.brand>
            : <ArcButton.ghost 
              fullWidth  
              onClick={toggleEditableRoute}> 
              Modify Route 
            </ArcButton.ghost>}
        </ArcStack>
        <ArcStack direction='row' spacing={1}>
          <ArcButton.ghost 
            fullWidth 
            disabled={editableRoute} 
            onClick={() => navigate(routesConfig.orders)}> 
            Exit 
          </ArcButton.ghost>
          <ArcButton.brand 
            fullWidth 
            type='submit' 
            disabled={editableRoute} 
            isLoading={isSubmitting}> 
            Next 
          </ArcButton.brand>
        </ArcStack>
        <ArcStack height={8} />
      </ArcStack>
    </ArcStack>
  );
};


export default EnterRouteForm;